import type { CSSProperties, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { definitionModule, type ModuleOptionnel } from '@artosera/shared';
import type { FormeApercu } from './catalogue.js';
import styles from './ApercuFantome.module.css';

/** Hauteurs relatives des barres : un rythme, jamais une donnée. */
const HAUTEURS = [42, 60, 35, 78, 55, 88, 66, 95] as const;

export interface ApercuFantomeProps {
  readonly module: ModuleOptionnel;
  readonly forme: FormeApercu;
  readonly galerieSlug: string;
}

/**
 * Aperçu FANTÔME d'un module à venir : des traits et des blocs gris, rien
 * d'autre. Aucun montant, aucune date, aucun nom : un chiffre plausible serait
 * pris pour une donnée de la galerie. L'étiquette « Exemple » le dit, et le
 * lien mène à la fiche du module — jamais à son écran, qui reste fermé.
 */
export function ApercuFantome({ module, forme, galerieSlug }: ApercuFantomeProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const nom = t(definitionModule(module).libelle);

  return (
    <Link
      className={styles.fantome}
      to={`/${galerieSlug}/modules/${module}`}
      data-ui="module-ghost"
      data-forme={forme}
    >
      <span className={styles.haut}>
        <span>{forme === 'vignettes' ? t('regie:modulesAVenir.bientot.apercuModule') : nom}</span>
        <span className={styles.etiquette}>{t('regie:modulesAVenir.exemple')}</span>
      </span>
      {forme === 'barres' ? (
        <>
          <span className={styles.titre}>{t('regie:modulesAVenir.regard.libelle')}</span>
          <span className={styles.trait} aria-hidden="true" />
          <span className={styles.barres} aria-hidden="true">
            {HAUTEURS.map((hauteur) => (
              <span key={hauteur} style={{ '--h': `${hauteur}%` } as CSSProperties} />
            ))}
          </span>
        </>
      ) : null}
      {forme === 'vignettes' ? (
        <>
          <span className={styles.titre}>{nom}</span>
          <span className={styles.vignettes} aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className={styles.ligne} aria-hidden="true" />
        </>
      ) : null}
      {forme === 'lignes' ? (
        <span className={styles.lignes} aria-hidden="true">
          {[0, 1, 2].map((index) => (
            <span key={index} className={styles.rangee}>
              <span className={styles.pastille} />
              <span className={styles.trait} />
            </span>
          ))}
        </span>
      ) : null}
    </Link>
  );
}
